// Product.jsx
import React, { useContext, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { FaTh, FaList } from 'react-icons/fa';
import { UserContext } from "../../../context/UserContext";
import apiFetch, { safeJson } from '../../../context/api';
import ButtonBar from '../../Elements/ButtonBar';
import BulkActionsBar from '../../Elements/BulkActionsBar';
import ProductModal from './ProductModal';
import StockUpdateModal from './StockUpdateModal';
import StockLocationsModal from './StockLocationsModal';
import ProductGrid from './ProductGrid';
import ProductTable from './ProductTable';
import useProducts from './useProducts';
import useMediaQuery from '../../hooks/useMediaQuery';
import './Products.css';

/** Si el texto del buscador coincide con una categoría, devuelve su nombre para filtrar en servidor. */
function resolveCategoryNameFromSearch(searchInput, categories) {
  const q = searchInput.trim().toLowerCase();
  if (!q) return null;

  const list = categories || [];
  const exact = list.find((cat) => (cat?.name || '').toLowerCase() === q);
  if (exact) return exact.name;

  const partial = list.filter((cat) => (cat?.name || '').toLowerCase().includes(q));
  if (partial.length === 1) return partial[0].name;

  return null;
}

function uploadFileWithProgress({ url, file, method = 'PUT', headers = {}, onProgress }) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    });
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve(xhr);
      else reject(new Error(`Upload failed: ${xhr.status}`));
    });
    xhr.addEventListener('error', () => reject(new Error('Network error during upload')));
    xhr.open(method, url);
    Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.send(file);
  });
}

function applyLogFilter(rows, filter) {
  if (!rows) return [];
  return rows.filter(r => {
    if (filter === 'all') return true;
    if (filter === 'created') return r.status === 'created' && (!r.warnings || r.warnings.length === 0);
    if (filter === 'updated') return r.status === 'updated';
    if (filter === 'warning') return (r.warnings && r.warnings.length > 0);
    if (filter === 'error') return r.status === 'error';
    return true;
  });
}

function downloadFilteredCSV(importLog, filter) {
  const rows = applyLogFilter(importLog.rows, filter);
  const filterLabel = { all: 'todas', created: 'creados', updated: 'actualizados', warning: 'avisos', error: 'errores' }[filter] || filter;
  const headers = ['Fila', 'Estado', 'Nombre', 'Categoría', 'Avisos', 'Error'];
  const escape = v => {
    if (v == null) return '';
    const s = String(v).replace(/"/g, '""');
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s}"` : s;
  };
  const lines = [
    headers.join(','),
    ...rows.map(r => [
      r.row ?? '',
      r.status ?? '',
      r.name ?? '',
      r.category ?? '',
      (r.warnings || []).join(' | '),
      r.error ?? '',
    ].map(escape).join(',')),
  ];
  const blob = new Blob(['\uFEFF' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `import-log-${importLog.log_id}-${filterLabel}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function normalizeImportLogPayload(data, fallbackId) {
  if (!data || data.error) return data;
  if (Array.isArray(data)) {
    return {
      log_id: fallbackId,
      rows: data,
      created_count: 0,
      updated_count: 0,
      warning_count: 0,
      error_count: data.filter(r => r.status === 'error').length,
      total_rows: data.length,
    };
  }
  return {
    ...data,
    log_id: data.log_id || fallbackId,
  };
}

const Product = () => {
  const { pathname } = useLocation();
  const isAlmacenPage = pathname === '/almacen';
  const isCartaPage = pathname === '/carta';
  const isReadOnlyPage = isCartaPage;
  const [token, , ownedShops, venueID, , , email, accessPrivileges, , , , , , , canManageProducts] = useContext(UserContext);
  // Quién puede editar/borrar productos y categorías: OWNER/SUPERADMIN siempre,
  // STAFF solo si tiene el permiso can_manage_products.
  const _role = (accessPrivileges || '').toLowerCase();
  const canEditProducts = _role === 'owner' || _role === 'superadmin' || (_role === 'staff' && !!canManageProducts);
  const [activeModal, setActiveModal] = useState(false);
  const [productId, setProductId] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);
  const [showDeleteCategoryModal, setShowDeleteCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [categoryToEdit, setCategoryToEdit] = useState(null);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [categoryRefreshTrigger, setCategoryRefreshTrigger] = useState(0);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'
  const [searchInput, setSearchInput] = useState("");
  // Debounced copy used only for the SERVER-side triggers (category resolution
  // + expand-to-500). The instant client-side filter still uses searchInput, so
  // typing stays responsive while we avoid refetching on every keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [visibleProductIds, setVisibleProductIds] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [categories, setCategories] = useState([]);
  const isMd = useMediaQuery('(min-width: 768px)');

  // Import job states (same system as socios)
  const [lastImportLogId, setLastImportLogId] = useState(() => localStorage.getItem('lastProductImportLogId') || null);
  const [importLog, setImportLog] = useState(null);
  const [showLogModal, setShowLogModal] = useState(false);
  const [loadingLog, setLoadingLog] = useState(false);
  const [logFilter, setLogFilter] = useState('all');
  const [showImportProgressModal, setShowImportProgressModal] = useState(false);
  const [importPhase, setImportPhase] = useState('idle'); // idle | uploading | processing | done | error
  const [importJobStatus, setImportJobStatus] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [importStatusMessage, setImportStatusMessage] = useState('');
  const pollTimerRef = useRef(null);

  const importHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'x-venue-id': venueID,
  }), [token, venueID]);

  const clearImportPoll = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearImportPoll(), [clearImportPoll]);

  const handleCategoryChange = useCallback((cat) => setSelectedCategory(cat), []);

  // Buscador por categoría: prioridad sobre la píldora activa (carga surtido completo en servidor).
  const searchResolvedCategory = useMemo(
    () => resolveCategoryNameFromSearch(debouncedSearch, categories),
    [debouncedSearch, categories]
  );

  // When a named category pill is active, ask the backend to filter server-side.
  // "Todas" and "Sin categoría" keep the existing client-side behaviour.
  const activeCategoryName = useMemo(() => {
    if (searchResolvedCategory) return searchResolvedCategory;
    if (!selectedCategory || selectedCategory === 'All' || selectedCategory === 'Sin categoría') return null;
    return selectedCategory;
  }, [searchResolvedCategory, selectedCategory]);

  const expandForSearch = useMemo(
    () => Boolean(debouncedSearch.trim() && !searchResolvedCategory),
    [debouncedSearch, searchResolvedCategory]
  );

  // Productos: exclude dispensario. Almacen: all products.
  const productListOptions = useMemo(() => ({
    ...(isAlmacenPage ? {} : { excludeDispensario: true }),
    ...(activeCategoryName ? { categoryName: activeCategoryName } : {}),
    ...(expandForSearch ? { expandForSearch: true } : {}),
  }), [isAlmacenPage, activeCategoryName, expandForSearch]);

  const { products, loading: productsLoading, errorMessage: productsError, deleteProduct, refreshProducts, page: productPage, hasMore: productHasMore, goToPage: goToProductPage } = useProducts(token, venueID, productListOptions);
  const loaded = !productsLoading;

  // Desglose de stock por ubicación (almacén principal / armarios / neveras) para
  // mostrarlo en cada producto. Una sola llamada al board trae todos los productos.
  const [stockByLoc, setStockByLoc] = useState({}); // product_id -> [{id,name,kind,qty}]
  const fetchBoard = useCallback(async () => {
    if (!venueID || !token) return;
    try {
      const res = await apiFetch(`/v1/stock/board?venue_id=${venueID}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const data = await safeJson(res);
      const locById = {};
      for (const l of (data.locations || [])) locById[String(l.id)] = l;
      const map = {};
      for (const p of (data.products || [])) {
        const arr = [];
        // DISPONIBLE por ubicación (físico − carritos abiertos); fallback al físico.
        for (const [locId, qty] of Object.entries(p.available_by_location ?? p.by_location ?? {})) {
          const q = Number(qty) || 0;
          if (q === 0) continue;
          const loc = locById[String(locId)];
          arr.push({ id: locId, name: loc?.name || '—', kind: loc?.kind || '', qty: q });
        }
        arr.sort((a, b) => (a.kind === 'principal' ? -1 : b.kind === 'principal' ? 1 : a.name.localeCompare(b.name)));
        map[String(p.id)] = arr;
      }
      setStockByLoc(map);
    } catch { /* no-op: el desglose es informativo */ }
  }, [venueID, token]);
  useEffect(() => { fetchBoard(); }, [fetchBoard]);

  // Combine error messages
  useEffect(() => {
    if (productsError) {
      setErrorMessage(productsError);
    }
  }, [productsError]);

  // Fetch categories from server (shared across all child views)
  useEffect(() => {
    if (!token || !venueID) return;
    const fetchCategories = async () => {
      try {
        const { data } = await axios.get('/v1/categories', {
          headers: { Authorization: `Bearer ${token}`, ...(venueID && { 'x-venue-id': venueID }) }
        });
        setCategories(data);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };
    fetchCategories();
  }, [token, venueID, categoryRefreshTrigger]);

  // Derive unique categories from products.
  const categoriesFromProducts = useMemo(() => {
    const categoryMap = new Map();
    products.forEach(product => {
      if (product.category && product.category.id) {
        categoryMap.set(product.category.id, product.category);
      }
    });
    return Array.from(categoryMap.values());
  }, [products]);

  // Search handler - búsqueda automática
  const handleSearchInput = (e) => {
    setSearchInput(e.target.value);
  };

  // Products to display (filtered by search if active)
  const displayProducts = useMemo(() => {
    if (!searchInput.trim()) {
      return products;
    }
    const searchTerm = searchInput.toLowerCase();
    return products.filter(product => {
      const name = product.name?.toLowerCase() || '';
      const category = product.category?.name?.toLowerCase() || '';
      const id = product.id?.toString().toLowerCase() || '';
      return name.includes(searchTerm) || category.includes(searchTerm) || id.includes(searchTerm);
    });
  }, [products, searchInput]);

  useEffect(() => {
    setSelectedProductIds((prev) => prev.filter((id) => products.some((product) => String(product.id) === String(id))));
  }, [products]);

  // Reset category filter and category list when venue changes so we don't show stale pills
  useEffect(() => {
    setSelectedCategory('All');
    setCategories([]);
  }, [venueID]);

  // Modal toggle and edit handler.
  const handleModal = () => {
    setActiveModal(!activeModal);
    if (!activeModal) {
      // ✅ Limpiar productId cuando se cierra el modal
      setProductId(null);
    }
  };
  const handleEdit = (id) => {
    setProductId(id);
    setActiveModal(true);
  };

  // Actualizar stock (entrar/ajustar/historial) — modal por producto
  const [stockProduct, setStockProduct] = useState(null);
  const [showLocations, setShowLocations] = useState(false);
  const handleStock = (id) => {
    const p = (products || []).find((x) => String(x.id) === String(id));
    if (p) setStockProduct(p);
  };



  // Delete handler: calls custom hook's delete function.
  const handleDelete = async (id) => {
    if (window.confirm("¿Estás seguro de que quieres eliminar este producto?")) {
      try {
        await deleteProduct(id);
        setErrorMessage(""); // Limpiar errores si fue exitoso
      } catch (error) {
        setErrorMessage("Error al eliminar el producto");
      }
    }
  };

  const toggleSelectedProduct = (id) => {
    setSelectedProductIds((prev) =>
      prev.some((currentId) => String(currentId) === String(id))
        ? prev.filter((currentId) => String(currentId) !== String(id))
        : [...prev, id]
    );
  };

  const clearSelectedProducts = () => setSelectedProductIds([]);

  const selectVisibleProducts = () => {
    if (visibleProductIds && visibleProductIds.length > 0) {
      setSelectedProductIds(visibleProductIds);
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedProductIds.length) return;
    const totalCount = selectedProductIds.length;
    if (!window.confirm(`¿Eliminar ${totalCount} productos seleccionados?`)) return;

    setIsBulkDeleting(true);
    const results = await Promise.allSettled(selectedProductIds.map((id) => deleteProduct(id)));
    const failedCount = results.filter((result) => result.status === 'rejected').length;

    setIsBulkDeleting(false);
    setSelectedProductIds([]);

    if (failedCount > 0) {
      setErrorMessage(`Se eliminaron ${totalCount - failedCount} productos y ${failedCount} fallaron.`);
      return;
    }

    setErrorMessage("");
  };

  // Funciones para manejar categorías
  const axiosHeaders = useMemo(() => ({
    Authorization: `Bearer ${token}`,
    ...(venueID && { 'x-venue-id': venueID })
  }), [token, venueID]);

  const handleSaveNewCategory = async () => {
    const name = newCategoryName.trim();
    if (!name || !token || !venueID) return;

    try {
      await axios.post(
        '/v1/categories',
        { name, parent_id: null },
        { headers: axiosHeaders }
      );
      setNewCategoryName("");
      setShowNewCategoryInput(false);
      // Recargar productos para actualizar categorías
      refreshProducts();
      // Trigger para recargar categorías en ProductGrid
      setCategoryRefreshTrigger(prev => prev + 1);
    } catch (err) {
      console.error('Failed to create category:', err);
      const errorMsg = err.response?.data?.detail || 'Error al crear la categoría';
      alert(errorMsg);
    }
  };

  const handleDeleteCategory = async (category) => {
    if (!token || !category) {
      console.error('No token or category provided', { token: !!token, category });
      alert('Error: No se pudo obtener la información de la categoría');
      return;
    }

    const confirmDelete = window.confirm(`¿Estás seguro de que quieres eliminar la categoría "${category.name}"?`);
    if (!confirmDelete) {
      return;
    }

    try {
      console.log('Deleting category:', category);
      const deleteHeaders = {
        Authorization: `Bearer ${token}`,
        ...(venueID && { 'x-venue-id': venueID })
      };
      console.log('Delete headers:', deleteHeaders);
      const response = await axios.delete(`/v1/categories/${category.id}`, {
        headers: deleteHeaders
      });
      console.log('Delete response:', response);
      setCategoryToDelete(null);
      setShowDeleteCategoryModal(false);
      // Recargar productos para actualizar categorías
      refreshProducts();
      // Trigger para recargar categorías en ProductGrid
      setCategoryRefreshTrigger(prev => prev + 1);
      alert('Categoría eliminada exitosamente');
    } catch (err) {
      console.error("Error deleting category:", err);
      console.error("Error response:", err.response);
      const errorMsg = err.response?.data?.detail || err.message || 'Error al eliminar la categoría';
      alert(`Error: ${errorMsg}`);
      // No cerrar el modal si hay error para que el usuario pueda intentar de nuevo
    }
  };

  const handleRenameCategory = async (category, nextName) => {
    if (!token || !category || !nextName.trim()) {
      return;
    }

    try {
      await axios.patch(
        `/v1/categories/${category.id}`,
        { name: nextName.trim() },
        { headers: axiosHeaders }
      );
      setCategoryToEdit(null);
      setShowEditCategoryModal(false);
      refreshProducts();
      setCategoryRefreshTrigger(prev => prev + 1);
      alert('Categoría actualizada exitosamente');
    } catch (err) {
      console.error('Error renaming category:', err);
      const errorMsg = err.response?.data?.detail || 'Error al actualizar la categoría';
      alert(errorMsg);
    }
  };

  const openEditModal = async () => {
    try {
      const { data } = await axios.get('/v1/categories', {
        headers: axiosHeaders
      });
      if (data.length === 0) {
        alert('No hay categorías para editar');
        return;
      }
      setShowEditCategoryModal(true);
    } catch (err) {
      console.error('Error fetching categories:', err);
      alert('Error al cargar las categorías');
    }
  };

  const openDeleteModal = async () => {
    // Obtener categorías para el modal
    try {
      const { data } = await axios.get('/v1/categories', {
        headers: axiosHeaders
      });
      if (data.length === 0) {
        alert('No hay categorías para eliminar');
        return;
      }
      setShowDeleteCategoryModal(true);
    } catch (err) {
      console.error('Error fetching categories:', err);
      alert('Error al cargar las categorías');
    }
  };

  // Import job polling
  const startImportJobPolling = useCallback((jobId) => {
    clearImportPoll();
    const pollOnce = async () => {
      try {
        const res = await axios.get(`/v1/clients/import-jobs/${jobId}`, { headers: importHeaders() });
        const status = res.data;
        setImportJobStatus(status);
        if (status.status === 'completed') {
          clearImportPoll();
          setImportPhase('done');
          setImportStatusMessage(
            `Importación completada: ${status.created_count} productos` +
            (status.error_count ? `, ${status.error_count} errores` : '') +
            (status.warning_count ? `, ${status.warning_count} avisos` : '') + '.'
          );
          refreshProducts();
          setCategoryRefreshTrigger(prev => prev + 1);
        } else if (status.status === 'failed') {
          clearImportPoll();
          setImportPhase('error');
          setImportStatusMessage(status.error_message || 'La importación falló.');
        } else {
          const processed = (status.created_count || 0) + (status.error_count || 0);
          setImportStatusMessage(`Procesando productos… ${processed}`);
        }
      } catch (pollError) {
        console.warn('Import job poll failed:', pollError);
      }
    };
    pollOnce();
    pollTimerRef.current = setInterval(pollOnce, 3000);
  }, [clearImportPoll, importHeaders, refreshProducts]);

  // Bulk Upload Handler — uses same import jobs system as socios
  const handleBulkUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.zip')) {
      alert('Por favor, sube un archivo .zip');
      event.target.value = null;
      return;
    }

    try {
      setErrorMessage('');
      clearImportPoll();
      setShowImportProgressModal(true);
      setImportPhase('uploading');
      setUploadProgress(0);
      setImportJobStatus(null);
      setImportStatusMessage('Preparando importación…');

      const createRes = await axios.post('/v1/clients/import-jobs', {
        filename: file.name,
        size_bytes: file.size,
        content_type: 'application/zip',
        job_type: 'products',
      }, { headers: importHeaders() });

      const { job_id: jobId, upload } = createRes.data;
      setLastImportLogId(jobId);
      localStorage.setItem('lastProductImportLogId', jobId);
      setImportStatusMessage('Subiendo archivo…');

      if (upload.mode === 's3') {
        await uploadFileWithProgress({
          url: upload.url,
          file,
          method: upload.method || 'PUT',
          headers: upload.headers || { 'Content-Type': 'application/zip' },
          onProgress: setUploadProgress,
        });
        setUploadProgress(100);
        await axios.post(`/v1/clients/import-jobs/${jobId}/confirm-upload`, null, { headers: importHeaders() });
      } else {
        const formData = new FormData();
        formData.append('file', file);
        await axios.post(upload.url, formData, {
          headers: importHeaders(),
          timeout: 10 * 60 * 1000,
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
            }
          },
        });
        setUploadProgress(100);
      }

      setImportPhase('processing');
      setImportStatusMessage('Archivo recibido. Procesando productos en segundo plano…');
      await axios.post(`/v1/clients/import-jobs/${jobId}/start`, null, { headers: importHeaders() });
      startImportJobPolling(jobId);
    } catch (error) {
      console.error('Bulk upload error:', error);
      clearImportPoll();
      setImportPhase('error');
      const msg = error.response?.data?.detail || error.message || 'Error en la carga masiva';
      setImportStatusMessage(msg);
      setErrorMessage(msg);
    } finally {
      event.target.value = null;
    }
  };

  const openImportLog = async () => {
    if (!lastImportLogId) return;
    setLoadingLog(true);
    setShowLogModal(true);
    setImportLog(null);
    setLogFilter('all');
    try {
      let res;
      try {
        res = await axios.get(`/v1/clients/import-jobs/${lastImportLogId}/log`, {
          headers: importHeaders(),
        });
      } catch (jobLogError) {
        if (jobLogError.response?.status === 404) {
          // Try legacy log endpoint as fallback
          try {
            res = await axios.get(`/v1/clients/import-logs/${lastImportLogId}`, {
              headers: importHeaders(),
            });
          } catch (legacyErr) {
            // Both endpoints returned 404 — log was lost (container restart wiped local files)
            setImportLog({ error: 'El log de esta importación ya no está disponible (el servidor fue reiniciado). Realiza una nueva importación para ver el resultado.' });
            setLoadingLog(false);
            return;
          }
        } else {
          throw jobLogError;
        }
      }
      setImportLog(normalizeImportLogPayload(res.data, lastImportLogId));
    } catch (e) {
      setImportLog({ error: e.response?.data?.detail || 'No se pudo cargar el log' });
    } finally {
      setLoadingLog(false);
    }
  };

  const closeImportProgressModal = () => {
    if (importPhase === 'processing') {
      const leave = window.confirm(
        'La importación sigue en segundo plano. ¿Cerrar este panel? Puedes ver el resultado con "Ver Log Import".'
      );
      if (!leave) return;
      clearImportPoll();
    }
    setShowImportProgressModal(false);
  };


  // Menu bar configuration
  const menu_bar = [
    {
      title: isAlmacenPage ? 'Almacen' : isCartaPage ? 'Carta' : 'Productos',
      buttons: isReadOnlyPage ? [] : [
        {
          label: 'Crear Producto',
          color: 'green',
          onClick: () => {
            setProductId(null);
            setActiveModal(true);
          },
        },
        {
          label: 'Subir ZIP',
          color: 'purple',
          onClick: () => document.getElementById('bulk-upload-input').click(),
        },
        ...(lastImportLogId ? [{ label: '📋 Ver Log Import', color: 'slate', onClick: openImportLog }] : []),
        {
          label: 'Crear Categoría',
          color: 'blue',
          onClick: () => setShowNewCategoryInput(true),
        },
        {
          label: 'Editar Categoría',
          color: 'blue',
          onClick: openEditModal,
        },
        {
          label: 'Eliminar Categoría',
          color: 'red',
          onClick: openDeleteModal,
        },
      ],
      viewToggle: (
        <div className="flex gap-1 mr-2">
          <input
            type="file"
            id="bulk-upload-input"
            accept=".zip"
            style={{ display: 'none' }}
            onChange={handleBulkUpload}
          />
          <button
            onClick={() => setViewMode('cards')}
            className={`p-2 rounded transition-all ${viewMode === 'cards'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            title="Vista de tarjetas"
          >
            <FaTh />
          </button>
          <button
            onClick={() => setViewMode('table')}
            className={`p-2 rounded transition-all ${viewMode === 'table'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            title="Vista de tabla"
          >
            <FaList />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="wa-almacen-page">
      <div className="flex flex-col gap-3 sm:gap-4 max-w-full overflow-x-hidden">
        {menu_bar.map((menuItem, index) =>
          isAlmacenPage ? (
            <React.Fragment key={index}>
              {/* Desktop: usa el ButtonBar normal (solo ≥ lg) */}
              <div className="hidden lg:block">
                <ButtonBar
                  title={menuItem.title}
                  buttons={menuItem.buttons}
                  viewToggle={menuItem.viewToggle}
                />
              </div>
              {/* Móvil / Tablet: botones alargados (igual que Socios) */}
              <div className="wa-almacen-hero block lg:hidden p-4 mb-4 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="space-y-2">
                    <span className="wa-almacen-badge">Inventario central</span>
                    <h2 className="wa-almacen-title text-xl sm:text-2xl">{menuItem.title}</h2>
                  </div>
                  {menuItem.viewToggle && (
                    <div className="flex items-center">
                      {menuItem.viewToggle}
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  {menuItem.buttons.map((button, i) => (
                    <button
                      key={i}
                      className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 text-white rounded-lg text-sm font-semibold shadow-sm hover:shadow transition-all active:scale-95 ${
                        button.color === 'green'
                          ? 'bg-green-500 hover:bg-green-600'
                          : button.color === 'purple'
                          ? 'bg-purple-600 hover:bg-purple-700'
                          : button.color === 'blue'
                          ? 'bg-blue-500 hover:bg-blue-600'
                          : button.color === 'red'
                          ? 'bg-red-500 hover:bg-red-600'
                          : button.color === 'slate'
                          ? 'bg-slate-600 hover:bg-slate-700'
                          : 'bg-blue-500 hover:bg-blue-600'
                      }`}
                      onClick={button.onClick}
                    >
                      {button.label}
                    </button>
                  ))}
                </div>
              </div>
            </React.Fragment>
          ) : (
            <ButtonBar
              key={index}
              title={menuItem.title}
              buttons={menuItem.buttons}
              viewToggle={menuItem.viewToggle}
            />
          )
        )}
      </div>

      {errorMessage && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{errorMessage}</span>
          <button
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
            onClick={() => setErrorMessage("")}
          >
            <span className="text-red-700">×</span>
          </button>
        </div>
      )}

      {!isReadOnlyPage ? (
        <BulkActionsBar
          selectedCount={selectedProductIds.length}
          onClear={clearSelectedProducts}
          onSelectAll={selectVisibleProducts}
          onDelete={handleBulkDelete}
          deleteLabel="Eliminar productos"
          disabled={isBulkDeleting}
        />
      ) : null}

      {/* Buscador */}
      {isAlmacenPage ? (
        <div className="wa-almacen-hero p-4 sm:p-6 space-y-4">
          <span className="wa-almacen-badge">Inventario y surtido</span>
          <div className="space-y-2">
            <h2 className="wa-almacen-title">Almacén</h2>
            <p className="wa-almacen-subtitle">
              Gestiona stock, categorías y catálogo con la misma presentación limpia y editorial de la portada.
            </p>
          </div>
          <div className="flex items-center justify-between gap-3">
            <div className="w-full">
              <input
                className="wa-almacen-search text-sm focus:outline-none active:outline-none border border-gray-300 w-full h-12 px-4 rounded"
                onChange={handleSearchInput}
                placeholder="Buscar por: Nombre, Categoría o ID"
                value={searchInput}
              />
            </div>
            {!isReadOnlyPage && canEditProducts && (
              <button
                onClick={() => setShowLocations(true)}
                className="flex-shrink-0 rounded-lg border border-gray-300 bg-white px-4 h-12 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Ubicaciones
              </button>
            )}
            <div className="hidden lg:flex items-center flex-shrink-0">
              {menu_bar[0]?.viewToggle}
            </div>
          </div>
        </div>
      ) : (
        <div className='bg-white justify-left shadow-2xl'>
          <div className="bg-white p-3 sm:p-4 rounded-lg flex items-center border border-gray-200 justify-between rounded">
            <input
              className="text-sm focus:outline-none active:outline-none border border-gray-300 w-full h-10 pl-11 pr-4 rounded"
              onChange={handleSearchInput}
              placeholder="Buscar por: Nombre, Categoría o ID"
              value={searchInput}
            />
          </div>
        </div>
      )}

      {isAlmacenPage ? (
        <div className="min-w-0">
          {viewMode === 'cards' ? (
            <div className="wa-almacen-panel p-3 sm:p-4 flex-auto min-w-0">
              <ProductGrid
                products={displayProducts}
                loading={!loaded}
                onEdit={handleEdit}
                onStock={handleStock}
                onDelete={handleDelete}
              canEdit={canEditProducts}
                selectedIds={selectedProductIds}
                onToggleSelect={toggleSelectedProduct}
                token={token}
                refreshTrigger={categoryRefreshTrigger}
                readOnly={isReadOnlyPage}
                searchQuery={searchInput}
                onVisibleProductsChange={setVisibleProductIds}
                selectedCategory={selectedCategory}
                onCategoryChange={handleCategoryChange}
                categories={categories}
              stockByLoc={stockByLoc}
                landscapeCols={5}
              />
            </div>
          ) : (
            <ProductTable
              products={displayProducts}
              loading={!loaded}
              onEdit={handleEdit}
                onStock={handleStock}
              onDelete={handleDelete}
              canEdit={canEditProducts}
              selectedIds={selectedProductIds}
              onToggleSelect={toggleSelectedProduct}
              token={token}
              venueID={venueID}
              refreshTrigger={categoryRefreshTrigger}
              readOnly={isReadOnlyPage}
              searchQuery={searchInput}
              onVisibleProductsChange={setVisibleProductIds}
              selectedCategory={selectedCategory}
              onCategoryChange={handleCategoryChange}
              categories={categories}
              stockByLoc={stockByLoc}
            />
          )}
        </div>
      ) : (
        <div className="min-w-0">
          {!isMd ? (
            <ProductGrid
              products={displayProducts}
              loading={!loaded}
              onEdit={handleEdit}
                onStock={handleStock}
              onDelete={handleDelete}
              canEdit={canEditProducts}
              selectedIds={selectedProductIds}
              onToggleSelect={toggleSelectedProduct}
              token={token}
              refreshTrigger={categoryRefreshTrigger}
              readOnly={isReadOnlyPage}
              searchQuery={searchInput}
              onVisibleProductsChange={setVisibleProductIds}
              selectedCategory={selectedCategory}
              onCategoryChange={handleCategoryChange}
              categories={categories}
              stockByLoc={stockByLoc}
            />
          ) : viewMode === 'cards' ? (
            <div className="bg-white p-3 sm:p-4 rounded-lg shadow border border-gray-200 flex-auto min-w-0">
              <ProductGrid
                products={displayProducts}
                loading={!loaded}
                onEdit={handleEdit}
                onStock={handleStock}
                onDelete={handleDelete}
              canEdit={canEditProducts}
                selectedIds={selectedProductIds}
                onToggleSelect={toggleSelectedProduct}
                token={token}
                refreshTrigger={categoryRefreshTrigger}
                readOnly={isReadOnlyPage}
                searchQuery={searchInput}
                onVisibleProductsChange={setVisibleProductIds}
                selectedCategory={selectedCategory}
                onCategoryChange={handleCategoryChange}
                categories={categories}
              stockByLoc={stockByLoc}
              />
            </div>
          ) : (
            <ProductTable
              products={displayProducts}
              loading={!loaded}
              onEdit={handleEdit}
                onStock={handleStock}
              onDelete={handleDelete}
              canEdit={canEditProducts}
              selectedIds={selectedProductIds}
              onToggleSelect={toggleSelectedProduct}
              token={token}
              venueID={venueID}
              refreshTrigger={categoryRefreshTrigger}
              readOnly={isReadOnlyPage}
              searchQuery={searchInput}
              onVisibleProductsChange={setVisibleProductIds}
              selectedCategory={selectedCategory}
              onCategoryChange={handleCategoryChange}
              categories={categories}
              stockByLoc={stockByLoc}
            />
          )}
        </div>
      )}

      {/* Pagination */}
      {(productPage > 0 || productHasMore) && !searchInput && (
        <div className="flex items-center justify-between border-t border-gray-200 pt-3 mt-2 px-1">
          <span className="text-sm text-gray-500">
            Página {productPage + 1} · {products.length} productos
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => goToProductPage(productPage - 1)}
              disabled={productPage === 0 || productsLoading}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              ← Anterior
            </button>
            <button
              onClick={() => goToProductPage(productPage + 1)}
              disabled={!productHasMore || productsLoading}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Siguiente →
            </button>
          </div>
        </div>
      )}

      {!isReadOnlyPage ? (
        <ProductModal
          active={activeModal}
          handleModal={handleModal}
          token={token}
          productId={productId}
          productData={productId ? products.find(p => p.id === productId) : null}
          ownedShops={ownedShops}
          email={email}
          setErrorMessage={setErrorMessage}
          onSaved={refreshProducts}
          refreshTrigger={categoryRefreshTrigger}
          categories={categoriesFromProducts}
          onOpenStock={(id) => {
            const pid = id || productId;
            const p = (products || []).find((x) => String(x.id) === String(pid));
            setActiveModal(false);
            setProductId(null);
            if (p) setStockProduct(p);
          }}
        />
      ) : null}

      {/* Modal Actualizar Stock (entrar / ajustar / historial) */}
      {stockProduct && (
        <StockUpdateModal
          product={stockProduct}
          token={token}
          venueID={venueID}
          onClose={() => setStockProduct(null)}
          onUpdated={() => { refreshProducts(); fetchBoard(); }}
        />
      )}

      {/* Modal Ubicaciones de stock (almacén / neveras / armarios) */}
      {showLocations && (
        <StockLocationsModal
          token={token}
          venueID={venueID}
          onClose={() => setShowLocations(false)}
        />
      )}

      {/* Modal para Crear Categoría */}
      {!isReadOnlyPage && showNewCategoryInput && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md my-4 mx-2 sm:mx-4">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-800">Crear Categoría</h2>
                <button
                  onClick={() => {
                    setShowNewCategoryInput(false);
                    setNewCategoryName("");
                  }}
                  className="text-gray-500 hover:text-gray-800 text-2xl font-bold"
                >
                  ×
                </button>
              </div>
              <input
                type="text"
                placeholder="Nombre de la categoría"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && newCategoryName.trim()) {
                    handleSaveNewCategory();
                  }
                }}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none mb-4"
                autoFocus
              />
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowNewCategoryInput(false);
                    setNewCategoryName("");
                  }}
                  className="px-5 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-all"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSaveNewCategory}
                  disabled={!newCategoryName.trim()}
                  className="px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  Crear
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal para Eliminar Categoría */}
      {!isReadOnlyPage && showEditCategoryModal && (
        <CategoryEditModal
          active={showEditCategoryModal}
          onClose={() => {
            setShowEditCategoryModal(false);
            setCategoryToEdit(null);
          }}
          onSave={handleRenameCategory}
          categoryToEdit={categoryToEdit}
          setCategoryToEdit={setCategoryToEdit}
          token={token}
          venueID={venueID}
        />
      )}

      {/* Import progress modal — same UX as Socios */}
      {showImportProgressModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-800">Importación masiva</h2>
              <button
                onClick={closeImportProgressModal}
                className="text-gray-400 hover:text-gray-700 text-2xl leading-none"
              >×</button>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">{importStatusMessage}</p>

              {importPhase === 'uploading' && (
                <div>
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>Subiendo ZIP</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-green-500 transition-all duration-200"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              {importPhase === 'processing' && (
                <div className="space-y-2">
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 animate-pulse w-full" />
                  </div>
                  {importJobStatus && (
                    <div className="grid grid-cols-3 gap-2 text-center text-xs">
                      <div className="bg-green-50 rounded p-2">
                        <div className="font-bold text-green-800">{importJobStatus.created_count || 0}</div>
                        <div className="text-green-700">Creados</div>
                      </div>
                      <div className="bg-yellow-50 rounded p-2">
                        <div className="font-bold text-yellow-800">{importJobStatus.warning_count || 0}</div>
                        <div className="text-yellow-700">Avisos</div>
                      </div>
                      <div className="bg-red-50 rounded p-2">
                        <div className="font-bold text-red-800">{importJobStatus.error_count || 0}</div>
                        <div className="text-red-700">Errores</div>
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-gray-500">
                    Puedes cerrar este panel; la importación continúa en segundo plano.
                    Usa &quot;Ver Log Import&quot; para ver el resultado.
                  </p>
                </div>
              )}

              {importPhase === 'done' && (
                <div className="rounded-lg bg-green-50 border border-green-200 p-4 text-sm text-green-800">
                  {importStatusMessage}
                </div>
              )}

              {importPhase === 'error' && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">
                  {importStatusMessage}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                {(importPhase === 'done' || importPhase === 'error') && lastImportLogId && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowImportProgressModal(false);
                      openImportLog();
                    }}
                    className="px-4 py-2 bg-slate-700 hover:bg-slate-800 text-white rounded text-sm font-semibold"
                  >
                    Ver log
                  </button>
                )}
                <button
                  type="button"
                  onClick={closeImportProgressModal}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-sm font-semibold"
                >
                  {importPhase === 'processing' ? 'Cerrar panel' : 'Cerrar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import log modal — same UX as Socios */}
      {showLogModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-bold text-gray-800">📋 Log de Importación</h2>
                {importLog && !importLog.error && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    {importLog.timestamp ? new Date(importLog.timestamp).toLocaleString('es-ES') : ''} · ID: {importLog.log_id}
                  </p>
                )}
              </div>
              <button
                onClick={() => setShowLogModal(false)}
                className="text-gray-400 hover:text-gray-700 text-2xl leading-none"
              >×</button>
            </div>

            {loadingLog && (
              <div className="flex-1 flex items-center justify-center py-16 text-gray-500">
                Cargando log…
              </div>
            )}

            {!loadingLog && importLog?.error && (
              <div className="flex-1 p-6 text-red-600">
                Error: {importLog.error}
              </div>
            )}

            {!loadingLog && importLog && !importLog.error && (
              <>
                <div className="flex flex-wrap gap-3 p-4 border-b border-gray-100">
                  <span className="px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-800">
                    ✅ Creados: {importLog.created_count}
                  </span>
                  {importLog.updated_count > 0 && (
                    <span className="px-3 py-1 rounded-full text-sm font-semibold bg-blue-100 text-blue-800">
                      ♻️ Actualizados: {importLog.updated_count}
                    </span>
                  )}
                  <span className="px-3 py-1 rounded-full text-sm font-semibold bg-yellow-100 text-yellow-800">
                    ⚠️ Avisos: {importLog.warning_count}
                  </span>
                  <span className="px-3 py-1 rounded-full text-sm font-semibold bg-red-100 text-red-800">
                    ❌ Errores: {importLog.error_count}
                  </span>
                  <span className="px-3 py-1 rounded-full text-sm font-semibold bg-gray-100 text-gray-700">
                    Total filas: {importLog.total_rows}
                  </span>
                </div>

                {(importLog.errors_summary || []).length > 0 && (
                  <div className="mx-4 mt-4 rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-900">
                    <div className="font-semibold mb-1">Resumen de avisos</div>
                    {(importLog.errors_summary || []).slice(0, 5).map((msg, idx) => (
                      <p key={idx} className="mt-1">{msg}</p>
                    ))}
                  </div>
                )}

                <div className="flex gap-1 px-4 pt-3">
                  {[
                    { key: 'all', label: 'Todas' },
                    { key: 'created', label: 'Creados' },
                    { key: 'updated', label: 'Actualizados' },
                    { key: 'warning', label: 'Con avisos' },
                    { key: 'error', label: 'Errores' },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => setLogFilter(key)}
                      className={`px-3 py-1.5 rounded text-xs font-semibold transition-colors ${
                        logFilter === key
                          ? 'bg-slate-700 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                <div className="flex-1 overflow-y-auto px-4 py-3">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-gray-500 border-b border-gray-200">
                        <th className="py-2 pr-2 font-semibold w-10">#</th>
                        <th className="py-2 pr-2 font-semibold">Estado</th>
                        <th className="py-2 pr-2 font-semibold">Nombre</th>
                        <th className="py-2 pr-2 font-semibold">Categoría</th>
                        <th className="py-2 font-semibold">Avisos / Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {applyLogFilter(importLog.rows, logFilter).map((row, i) => {
                        const hasWarning = row.warnings && row.warnings.length > 0;
                        const isError = row.status === 'error';
                        const isUpdated = row.status === 'updated';
                        return (
                          <tr
                            key={i}
                            className={`border-b border-gray-50 ${
                              isError ? 'bg-red-50' : hasWarning ? 'bg-yellow-50' : isUpdated ? 'bg-blue-50' : ''
                            }`}
                          >
                            <td className="py-1.5 pr-2 text-gray-400">{row.row}</td>
                            <td className="py-1.5 pr-2">
                              <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${
                                isError ? 'bg-red-200 text-red-800' :
                                hasWarning ? 'bg-yellow-200 text-yellow-800' :
                                isUpdated ? 'bg-blue-200 text-blue-800' :
                                'bg-green-200 text-green-800'
                              }`}>
                                {isError ? 'error' : hasWarning ? 'aviso' : isUpdated ? 'actualizado' : '✓'}
                              </span>
                            </td>
                            <td className="py-1.5 pr-2 text-gray-700">{row.name || '—'}</td>
                            <td className="py-1.5 pr-2 text-gray-500">{row.category || '—'}</td>
                            <td className="py-1.5 text-gray-600">
                              {hasWarning && row.warnings.map((w, wi) => (
                                <div key={wi} className="text-yellow-700">⚠ {w}</div>
                              ))}
                              {row.error && <div className="text-red-700">✗ {row.error}</div>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {applyLogFilter(importLog.rows, logFilter).length === 0 && (
                    <p className="text-center text-gray-400 py-8">No hay filas con este filtro.</p>
                  )}
                </div>

                <div className="p-4 border-t border-gray-100 flex items-center justify-between gap-3 flex-wrap">
                  <span className="text-xs text-gray-400">
                    {applyLogFilter(importLog.rows, logFilter).length} fila{applyLogFilter(importLog.rows, logFilter).length !== 1 ? 's' : ''} mostradas
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => downloadFilteredCSV(importLog, logFilter)}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded text-sm font-semibold transition-colors"
                    >
                      ⬇ Descargar CSV{logFilter !== 'all' ? ` (${({ created: 'creados', updated: 'actualizados', warning: 'avisos', error: 'errores' })[logFilter]})` : ''}
                    </button>
                    <a
                      href={`data:application/json;charset=utf-8,${encodeURIComponent(JSON.stringify(importLog, null, 2))}`}
                      download={`import-log-${importLog.log_id}.json`}
                      className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded text-sm font-semibold transition-colors"
                    >
                      ⬇ JSON
                    </a>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal para Eliminar Categoría */}
      {showDeleteCategoryModal && (
        <CategoryDeleteModal
          active={showDeleteCategoryModal}
          onClose={() => {
            setShowDeleteCategoryModal(false);
            setCategoryToDelete(null);
          }}
          onDelete={(category) => {
            handleDeleteCategory(category);
          }}
          categoryToDelete={categoryToDelete}
          setCategoryToDelete={setCategoryToDelete}
          token={token}
          venueID={venueID}
        />
      )}
    </div>
  );
};

const CategoryEditModal = ({ active, onClose, onSave, categoryToEdit, setCategoryToEdit, token, venueID }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nextName, setNextName] = useState("");

  useEffect(() => {
    if (!active || !token || !venueID) {
      return;
    }

    const fetchCategories = async () => {
      try {
        const { data } = await axios.get('/v1/categories', {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(venueID && { 'x-venue-id': venueID })
          }
        });
        setCategories(data);
      } catch (err) {
        console.error('Error fetching categories:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [active, token, venueID]);

  useEffect(() => {
    setNextName(categoryToEdit?.name || "");
  }, [categoryToEdit]);

  if (!active) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-800">Editar Categoría</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-800 text-2xl font-bold"
            >
              ×
            </button>
          </div>
          {loading ? (
            <p className="text-gray-600">Cargando categorías...</p>
          ) : categories.length === 0 ? (
            <p className="text-gray-600">No hay categorías para editar</p>
          ) : (
            <>
              <p className="text-gray-600 mb-4">Selecciona la categoría que deseas renombrar:</p>
              <div className="max-h-48 overflow-y-auto mb-4 space-y-2">
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setCategoryToEdit(cat)}
                    className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all ${categoryToEdit?.id === cat.id
                        ? 'bg-blue-50 border-blue-500 text-blue-700'
                        : 'bg-white border-gray-300 text-gray-700 hover:border-blue-300'
                      }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
              <input
                type="text"
                value={nextName}
                onChange={(e) => setNextName(e.target.value)}
                placeholder="Nuevo nombre"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none mb-4"
                disabled={!categoryToEdit}
              />
            </>
          )}
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-5 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-all"
            >
              Cancelar
            </button>
            <button
              onClick={() => onSave(categoryToEdit, nextName)}
              disabled={!categoryToEdit || !nextName.trim() || loading}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              Guardar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Componente modal para eliminar categoría
const CategoryDeleteModal = ({ active, onClose, onDelete, categoryToDelete, setCategoryToDelete, token, venueID }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (active && token && venueID) {
      const fetchCategories = async () => {
        try {
          const { data } = await axios.get('/v1/categories', {
            headers: {
              Authorization: `Bearer ${token}`,
              ...(venueID && { 'x-venue-id': venueID })
            }
          });
          setCategories(data);
          setLoading(false);
        } catch (err) {
          console.error('Error fetching categories:', err);
          setLoading(false);
        }
      };
      fetchCategories();
    }
  }, [active, token, venueID]);

  if (!active) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-800">Eliminar Categoría</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-800 text-2xl font-bold"
            >
              ×
            </button>
          </div>
          {loading ? (
            <p className="text-gray-600">Cargando categorías...</p>
          ) : categories.length === 0 ? (
            <p className="text-gray-600">No hay categorías para eliminar</p>
          ) : (
            <>
              <p className="text-gray-600 mb-4">Selecciona la categoría que deseas eliminar:</p>
              <div className="max-h-60 overflow-y-auto mb-4 space-y-2">
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setCategoryToDelete(cat)}
                    className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all ${categoryToDelete?.id === cat.id
                        ? 'bg-red-50 border-red-500 text-red-700'
                        : 'bg-white border-gray-300 text-gray-700 hover:border-red-300'
                      }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </>
          )}
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-5 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-all"
            >
              Cancelar
            </button>
            <button
              onClick={() => {
                if (categoryToDelete) {
                  onDelete(categoryToDelete);
                }
              }}
              disabled={!categoryToDelete || loading}
              className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              Eliminar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Product;
